import className from "classnames/bind";
import { RhLink } from "@components";
import ArrowRight from "@/assets/svg/arrow-tail-right-no-padding.svg";
import { useMemo } from "react";

import styles from "./Button.module.scss";
const cx = className.bind(styles);

interface ButtonProps {
  url?: string;
  target?: string;
  type?: "button" | "submit" | "reset";
  size?: "default" | "small";
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onMouseEnter?: React.MouseEventHandler<any>;
  onMouseLeave?: React.MouseEventHandler<any>;
  onFocus?: React.FocusEventHandler<any>;
  onBlur?: React.FocusEventHandler<any>;
  label: React.ReactNode;
  ariaLabel?: string;
  disabled?: boolean;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  noIcon?: boolean;
  collapseOnMobile?: boolean;
  asDiv?: boolean;
  dataGtmSection?: string;
}

export default function Button({
  url,
  target,
  type,
  className,
  onClick,
  onMouseEnter,
  onMouseLeave,
  size = "default",
  onFocus,
  onBlur,
  label,
  ariaLabel,
  disabled = false,
  icon = ArrowRight,
  noIcon = false,
  collapseOnMobile = false,
  asDiv = false,
  dataGtmSection
}: ButtonProps) {

  const Tag = url ? RhLink : "button";
  const Icon = icon;
  const labelClass = useMemo(() => size === "default" ? "cta" : "text-s", [size]);
  // label can be markup (e.g. responsive label variants), which can't stand in
  // for aria-label — only a plain string is usable as the fallback
  const resolvedAriaLabel = ariaLabel || (typeof label === "string" ? label : undefined);

  // Standard link/button version
  if (!asDiv) {

    return (
      <Tag
        className={`button ${cx([
          "button",
          className,
          size,
          { "mobile-collapse": collapseOnMobile }
        ])}`}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
        aria-label={resolvedAriaLabel}
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        type={type}
        target={target ? target : undefined}
        href={url ? url : undefined}
        data-gtm-section={dataGtmSection}
      >
        <span className={cx([labelClass, "label"])}>{label}</span>
        {!noIcon && <Icon className={cx("icon")} />}
      </Tag>

    );

  }

  // Non-link version that returns a DIV instead of a link/button.
  // Used inside content blocks where the entire element is wrapped in a link
  else {

    return (
      <div
        className={`button ${cx([
          "button",
          className,
          size,
          { "mobile-collapse": collapseOnMobile }
        ])}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
        aria-label={resolvedAriaLabel}
      >
        <span className={cx([labelClass, "label"])}>{label}</span>
        {!noIcon && <Icon className={cx("icon")} />}
      </div>
    );

  }

}
